import { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  LinearProgress, 
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CloudUpload as UploadIcon, 
  DeleteOutline as DeleteIcon, 
  CheckCircleOutline as SuccessIcon 
} from '@mui/icons-material';
import Layout from '../components/layout/Calayout';

const DocumentUploadPage = () => {
  // State for form fields
  const [clientDetails, setClientDetails] = useState({
    clientName: '',
    panNumber: '',
    contactNumber: '',
    documentType: ''
  });

  // State for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Ref for file input
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files).map(file => ({
        id: `file-${Date.now()}-${file.name}`,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadProgress: 0,
        status: 'pending'
      }));

      setUploadedFiles(prev => [...prev, ...newFiles]);
      
      // Simulate upload process
      newFiles.forEach(file => simulateFileUpload(file));
    }
  };

  // Simulate file upload process
  const simulateFileUpload = (file) => {
    setUploadedFiles(prev => prev.map(f => 
      f.id === file.id ? { ...f, status: 'uploading' } : f
    ));

    const uploadSimulation = () => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadedFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { 
                ...f, 
                uploadProgress: progress,
                status: progress === 100 ? 'completed' : 'uploading' 
              } 
            : f
        ));

        if (progress >= 100) {
          clearInterval(interval);
        }
      }, 300);
    };

    uploadSimulation();
  };

  // Remove file from upload list
  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  // Handle form submission
  const handleSubmit = () => {
    // Validate form and files
    if (!clientDetails.clientName || !clientDetails.documentType || uploadedFiles.length === 0) {
      alert('Please fill in all required fields and upload at least one file');
      return;
    }

    // Prepare submission data
    const submissionData = {
      ...clientDetails,
      files: uploadedFiles
    };

    // TODO: Replace with actual API call
   
    alert('Documents uploaded successfully!');

    // Reset form
    setClientDetails({
      clientName: '',
      panNumber: '',
      contactNumber: '',
      documentType: ''
    });
    setUploadedFiles([]);
  };

  // Render file upload status
  
  const renderFileStatus = (file) => {
    // eslint-disable-next-line
    switch (file.status) {
      case 'pending':
        return <Typography color="text.secondary">Pending</Typography>;
      case 'uploading':
        return <LinearProgress variant="determinate" value={file.uploadProgress} />;
      case 'completed':
        return <Alert icon={<SuccessIcon />} severity="success">Uploaded</Alert>;
      case 'error':
        return <Alert severity="error">Upload Failed</Alert>;
    }
  };

  return (
    <Layout>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Document Upload
          </Typography>

          {/* Client Details */}
          <Grid container spacing={3} mt={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Client Name"
                variant="outlined"
                value={clientDetails.clientName}
                onChange={(e) => setClientDetails(prev => ({
                  ...prev,
                  clientName: e.target.value
                }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PAN Number"
                variant="outlined"
                value={clientDetails.panNumber}
                onChange={(e) => setClientDetails(prev => ({
                  ...prev,
                  panNumber: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Number"
                variant="outlined"
                value={clientDetails.contactNumber}
                onChange={(e) => setClientDetails(prev => ({
                  ...prev,
                  contactNumber: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={clientDetails.documentType}
                  onChange={(e) => setClientDetails(prev => ({
                    ...prev,
                    documentType: e.target.value
                  }))}
                  label="Document Type"
                  required
                >
                  <MenuItem value="Income Tax Return">Income Tax Return</MenuItem>
                  <MenuItem value="GST Audit">GST Audit</MenuItem>
                  <MenuItem value="Financial Statement">Financial Statement</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* File Upload Section */}
          <Box mt={3}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              multiple
            />
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Documents
            </Button>

            {/* Uploaded Files List */}
            {uploadedFiles.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle1">Uploaded Files</Typography>
                {uploadedFiles.map((file) => (
                  <Grid 
                    container 
                    spacing={2} 
                    alignItems="center" 
                    key={file.id}
                    mt={1}
                  >
                    <Grid item xs={4}>
                      <Typography variant="body2">{file.name}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        {(file.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      {renderFileStatus(file)}
                    </Grid>
                    <Grid item xs={1}>
                      <Tooltip title="Remove File">
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleRemoveFile(file.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
          </Box>

          {/* Submit Button */}
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
              disabled={!clientDetails.clientName || !clientDetails.documentType || uploadedFiles.length === 0}
            >
              Submit Documents
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default DocumentUploadPage;